<template>
    <app-layout>
        <div class="intelligence">
            <aside class="intelligence__sidebar">
                <div class="intelligence__brand">
                    <h2 class="intelligence__brand-title">{{ $t('pages.intelligence.menu_label') }}</h2>
                </div>
                <nav class="intelligence__nav">
                    <router-link
                        v-for="item in navItems"
                        :key="item.name"
                        :to="{ name: item.name }"
                        class="intelligence__nav-item"
                        activeClass="intelligence__nav-item--active"
                    >
                        {{ item.label }}
                    </router-link>
                </nav>
            </aside>

            <main class="intelligence__main">
                <header class="intelligence__header">
                    <div class="intelligence__header-text">
                        <h1 class="intelligence__title">{{ title }}</h1>
                        <p v-if="subtitle" class="intelligence__subtitle">{{ subtitle }}</p>
                    </div>
                    <div class="intelligence__filters">
                        <form-wrapper>
                            <form-row>
                                <form-cell>
                                    <date-range-field
                                        :formData="data"
                                        openSide="left"
                                        valuePath="dateRange"
                                        @change="onDateRangeChange($event)"
                                    ></date-range-field>
                                </form-cell>
                            </form-row>
                        </form-wrapper>
                    </div>
                </header>

                <div class="intelligence__content">
                    <slot></slot>
                </div>
            </main>
        </div>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import FormMixin from 'mixins/FormMixin'
    import dayjs from 'dayjs'

    export default {
        mixins: [FormMixin],

        components: {
            AppLayout,
        },

        props: {
            title: {
                type: String,
                required: true,
            },
            subtitle: {
                type: String,
                default: '',
            },
        },

        data() {
            return {}
        },

        computed: {
            navItems() {
                return [
                    {
                        name: 'intelligence.overview',
                        label: this.$t('pages.intelligence.nav.overview'),
                    },
                    {
                        name: 'intelligence.constraints',
                        label: this.$t('pages.intelligence.nav.constraints'),
                    },
                ]
            },
        },

        methods: {
            onDateRangeChange(event) {
                this.change(event)
                this.emitDateRange()
            },

            emitDateRange() {
                const { startDate, endDate } = this.data.dateRange || {}
                if (!startDate || !endDate) return

                this.$emit('date-range-change', {
                    fromDate: dayjs(startDate).format('YYYY-MM-DD'),
                    toDate: dayjs(endDate).format('YYYY-MM-DD'),
                })
            },
        },

        created() {
            const endDate = new Date()
            const startDate = new Date()
            startDate.setDate(startDate.getDate() - 30)

            this.data.dateRange = {
                startDate,
                endDate,
            }
        },

        mounted() {
            this.emitDateRange()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/variables';

    .intelligence {
        display: flex;
        min-height: calc(100vh - 80px);
        background: #f9fafb;
    }

    .intelligence__sidebar {
        width: 240px;
        flex-shrink: 0;
        background: #fff;
        border-right: 1px solid #e5e7eb;
        padding: 24px 0;
    }

    .intelligence__brand {
        padding: 0 20px 20px;
        border-bottom: 1px solid #e5e7eb;
        margin-bottom: 12px;
    }

    .intelligence__brand-title {
        margin: 0;
        font-size: 15px;
        font-weight: 700;
        color: #1e3a8a;
    }

    .intelligence__nav {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 0 12px;
    }

    .intelligence__nav-item {
        display: block;
        padding: 10px 12px;
        border-radius: 8px;
        color: #374151;
        text-decoration: none;
        font-size: 14px;
        font-weight: 500;
        transition: background 0.15s ease;

        &:hover {
            background: #f3f4f6;
        }

        &--active {
            background: #1e3a8a;
            color: #fff;
        }
    }

    .intelligence__main {
        flex: 1;
        min-width: 0;
        padding: 24px 32px;
    }

    .intelligence__header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 24px;
        margin-bottom: 24px;
        flex-wrap: wrap;
    }

    .intelligence__title {
        margin: 0 0 4px;
        font-size: 28px;
        font-weight: 700;
        color: #111827;
    }

    .intelligence__subtitle {
        margin: 0;
        font-size: 14px;
        color: #6b7280;
    }

    .intelligence__filters {
        min-width: 280px;
    }

    .intelligence__content {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }

    @media (max-width: 900px) {
        .intelligence {
            flex-direction: column;
        }

        .intelligence__sidebar {
            width: 100%;
            border-right: none;
            border-bottom: 1px solid #e5e7eb;
        }

        .intelligence__nav {
            flex-direction: row;
            flex-wrap: wrap;
        }

        .intelligence__main {
            padding: 16px;
        }
    }
</style>
